/* eslint-disable prettier/prettier */
import { useMemo } from "react";
import { EDB, Reception } from "$types";
import ReferenceType from "$types/ReferenceType";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";
import Text from "$components/Text";
import JTree from "$components/JTree/JTree";
import useReceptionForTree, { TreeReference } from "./useReceptionForTree";
import { normalizeArray } from "$utils/utils";

export const QTY_WIDTH = "60px"; // approx. 5 digits
export const UNIT_WIDTH = "70px";

type Props = {
  expandAllNodes?: boolean;
  integratedOrders?: EDBIntegratedOrders;
  edbProducts?: EDB["products"];
  noReceptionsFoundMessage?: string;
  onCheck?: (checkedItems: TreeReference[]) => void;
  onEdit?: () => Promise<void> | void;
  receptions: Reception[];
  showCheckbox: boolean;
  showCheckboxForProductQuantity: boolean;
  showExpandAll?: boolean;
};

// Helper function to normalize reception data structure
const normalizeReception = (reception: any) => {
  // Normalize the orders structure
  let normalizedOrders = [];
  if (reception.orders) {
    if (reception.orders.data && Array.isArray(reception.orders.data)) {
      // New structure: orders: { data: [...] }
      normalizedOrders = reception.orders.data.map((order: any) => ({
        id: order.id,
        ...order,
      }));
    } else if (Array.isArray(reception.orders)) {
      // Old structure: orders: [...]
      normalizedOrders = reception.orders;
    }
  }

  return {
    ...reception,
    orders: normalizedOrders,
  };
};

function JReceptionTree({
  expandAllNodes,
  integratedOrders,
  edbProducts,
  noReceptionsFoundMessage = "Aucune réception trouvée",
  onCheck,
  onEdit,
  receptions,
  showCheckbox,
  showCheckboxForProductQuantity,
  showExpandAll = true,
}: Props) {
  const { formatReceptionForTree } = useReceptionForTree({
    integratedOrders,
    edbProducts,
    onEdit,
    showCheckbox,
    showCheckboxForProductQuantity,
  });

  // Memoize receptionNodes to ensure it recalculates when receptions or dependencies change
  const receptionNodes = useMemo(() => {
    if (!receptions || receptions.length === 0) return [];
    
    return receptions
      .map((r) => {
        const normalizedReception = normalizeReception(r);

        const { orders, productQuantity, receptionReferences } =
          normalizedReception;

        const orderReferences =
          receptionReferences?.filter(
            (rr) => rr.referenceType === ReferenceType.ORDER
          ) || [];

        const productReferences =
          receptionReferences?.filter(
            (rr) => rr.referenceType === ReferenceType.PRODUCT
          ) || [];

        const isReferencingIncomplete =
          normalizeArray(orders)
            .flatMap((o) => o.suborders || [])
            .filter((suborder) => {
              const { reference: suborderReference, suborderProducts } = suborder;
              const suborderproductQuantity = (productQuantity || []).filter(
                (pq) => pq.suborderReference === suborderReference
              );
              const inOrderRefs = !!orderReferences
                .flatMap((or) =>
                  normalizeArray(or.orders || []).flatMap(
                    (o) => o.suborders || []
                  )
                )
                .find(
                  (suborder2) => suborder2?.reference === suborder?.reference
                );
              const inProductRefs = !!productReferences.find((productRef) =>
                (productRef.suborderReferences || []).includes(suborder.reference)
              );
              const atLeastOneReceived = suborderproductQuantity.some(
                (productQuantity) => {
                  return (
                    (suborderProducts || [])
                      .map((p) => p.reference)
                      .includes(productQuantity.productId) &&
                    productQuantity.receivedQuantity > 0
                  );
                }
              );
              return !inOrderRefs && !inProductRefs && atLeastOneReceived;
            }).length > 0;

        const result = formatReceptionForTree(
          isReferencingIncomplete,
          normalizedReception
        );

        return result;
      })
      .filter((node) => node !== null);
  }, [receptions, formatReceptionForTree]);

  const handleOnCheck = (checked: string[]) => {
    // Only receptionReference must be retrieved
    const checkedItems = checked.map((c) => JSON.parse(c) as TreeReference);
    onCheck?.(checkedItems);
  };

  return receptionNodes.length > 0 ? (
    <JTree
      expandAllNodes={expandAllNodes}
      nodes={receptionNodes}
      onCheck={handleOnCheck}
      showExpandAll={showExpandAll}
    />
  ) : (
    <Text>{noReceptionsFoundMessage}</Text>
  );
}

export default JReceptionTree;
